import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { ThemeProvider } from 'styled-components';
import nebulaTheme from '@nebula/theme';
import GlobalStyles from '@nebula/global-styles';
import PaymentOrchestrator from '../../components/PaymentOrchestrator';
import ProcessingSpinner from '../../components/PaymentProcessing/ProcessingSpinner';
import startSecureMode from '../../actions/app/startSecureMode';
import usePageView from '../../hooks/usePageView';
import { onRestartPageLoadECD } from '../../helpers/chooser/analytics';
import findFlexPlanByFamily from '../../helpers/flex-helpers/findFlexPlanByFamily';
import usePreviousSubscriptionFamily from '../../hooks/usePreviousSubscriptionFamily';
import { products, stateVariables } from '../../constants';
import { useGetEntitlementsQuery, useGetAllProductsQuery, useGetIsFlexUserQuery } from '../../services/entitlementsApi';

const PAGE_NAME = 'ProdMoveReactivatePage';

const RestartPage = () => {
  const dispatch = useDispatch();

  const {
    data: flexEligibleData,
    isLoading: isFlexEligibleLoading,
    isUninitialized: isFlexEligibleUninitialized,
  } = useGetIsFlexUserQuery();

  const isFlexEligibilityResolved = !isFlexEligibleUninitialized && !isFlexEligibleLoading;
  const isFlexFlow = isFlexEligibilityResolved && flexEligibleData?.isFlex === true;

  const {
    data: entitlementsData,
    isLoading: isEntitlementsLoading,
    isUninitialized: isEntitlementsUninitialized,
  } = useGetEntitlementsQuery(undefined, { skip: !isFlexFlow });
  const {
    data: allProductsData,
    isLoading: isProductsLoading,
    isUninitialized: isProductsUninitialized,
  } = useGetAllProductsQuery(undefined, { skip: !isFlexFlow });

  const allAvailableOffers = useSelector(state => state.offers);
  const { userPreviousSubscriptionFamily, isActiveUser: isPPCActiveUser } = usePreviousSubscriptionFamily();
  const subscriptionFamily = isFlexFlow ? entitlementsData?.family : userPreviousSubscriptionFamily;

  const getTargetOfferId = () => {
    switch (subscriptionFamily) {
      case products.credit_expert:
        return allAvailableOffers.credit_expert?.offerId;
      case products.identity:
        return allAvailableOffers.identity?.offerId;
      default:
        return null;
    }
  };

  const targetOfferId = isFlexFlow ? null : getTargetOfferId();

  const targetOffer =
    targetOfferId && allAvailableOffers?.data
      ? Object.values(allAvailableOffers.data).find(offer => offer.id === targetOfferId)
      : null;

  const targetPlan = isFlexFlow
    ? findFlexPlanByFamily(subscriptionFamily, allProductsData?.eligiblePlans, allProductsData?.isTrialEligible)
    : null;

  const upgradePlan = useMemo(
    () => (targetPlan ? { family: subscriptionFamily, product: targetPlan } : null),
    [targetPlan, subscriptionFamily]
  );

  const currentFlexSubscription = useMemo(
    () => ({
      family: subscriptionFamily,
      planCode: entitlementsData?.plans?.[0]?.planCode,
      freeTrial: Boolean(allProductsData?.isTrialEligible),
    }),
    [subscriptionFamily, entitlementsData, allProductsData]
  );

  const resolvedIsTrial = isFlexFlow ? (targetPlan?.trialLength ?? 0) > 0 : false;
  const isActiveUser = isFlexFlow ? entitlementsData?.active === true : isPPCActiveUser;
  const isFlexDataLoading =
    isFlexFlow &&
    (isEntitlementsUninitialized ||
      isEntitlementsLoading ||
      isProductsUninitialized ||
      isProductsLoading ||
      !entitlementsData ||
      !allProductsData);
  const isPageLoading =
    !isFlexEligibilityResolved || isFlexDataLoading || (!isFlexFlow && !userPreviousSubscriptionFamily);

  useEffect(() => {
    dispatch(startSecureMode());
  }, [dispatch]);

  useEffect(() => {
    if (!isActiveUser) {
      return;
    }

    dispatch(push('/', { [stateVariables.persistErrorsOnLocationChange]: true }));
  }, [dispatch, isActiveUser]);

  usePageView(PAGE_NAME, {
    reactivateSubscription: { subscription: subscriptionFamily },
  });

  useEffect(() => {
    if (!subscriptionFamily) {
      return;
    }

    onRestartPageLoadECD({
      reactivateSubscriptionFamily: subscriptionFamily,
    });
  }, [subscriptionFamily, targetOffer, targetPlan]);

  if (isPageLoading || isActiveUser) {
    return (
      <ThemeProvider theme={nebulaTheme}>
        <GlobalStyles />
        <ProcessingSpinner isWebview={false} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={nebulaTheme}>
      <GlobalStyles />
      <PaymentOrchestrator
        upgradeOffer={isFlexFlow ? null : targetOffer}
        upgradePlan={isFlexFlow ? upgradePlan : null}
        currentFlexSubscription={isFlexFlow ? currentFlexSubscription : null}
        isTrial={resolvedIsTrial}
        isFlexFlow={isFlexFlow}
        isRestart
      />
    </ThemeProvider>
  );
};

export default RestartPage;
